import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ApiError, errorText } from "@common/api";
import { getMe, getProfile, putProfile, queryKeys } from "../api/salvagers";
import { Button, ErrorState, Notice, Page, SettingsSection, TextField } from "@jkast90/kast-common/ui";
import { AccountPage as Account } from "@common/account";

/** The signed-in details and the display name: this site's profile, on its one account page. */
function Details() {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: queryKeys.me, queryFn: getMe });
  const profile = useQuery({
    queryKey: queryKeys.profile,
    queryFn: getProfile,
    // 404 means no profile yet, which is not an error worth retrying.
    retry: false,
  });
  const [displayName, setDisplayName] = useState("");
  const save = useMutation({
    mutationFn: putProfile,
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.profile, saved);
      queryClient.invalidateQueries({ queryKey: queryKeys.tables });
      setDisplayName("");
    },
  });

  const noProfileYet = profile.error instanceof ApiError && profile.error.status === 404;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (displayName.trim()) save.mutate(displayName.trim());
  };

  return (
    <>
      {me.error && (
        <ErrorState
          description={errorText(me.error, "Could not load your account.")}
          onRetry={() => void me.refetch()}
        />
      )}
      {me.data && (
        <SettingsSection title="Groups">
          <p>{me.data.groups.length ? me.data.groups.join(", ") : "none"}</p>
        </SettingsSection>
      )}
      <SettingsSection title="Display name">
        {profile.data && (
          <p>
            Display name: <strong>{profile.data.display_name}</strong>
          </p>
        )}
        {noProfileYet && <p className="kui-muted">No profile yet. Pick a display name.</p>}
        {profile.error && !noProfileYet && (
          <ErrorState
            description={errorText(profile.error, "Could not load your profile.")}
            onRetry={() => void profile.refetch()}
          />
        )}
        <form onSubmit={submit} className="kui-row">
          <TextField
            wrapperClassName="kui-grow"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Display name"
            maxLength={40}
            aria-label="Display name"
          />
          <Button variant="primary" type="submit" disabled={save.isPending || !displayName.trim()}>
            Save
          </Button>
        </form>
        {save.error && <Notice tone="error">{errorText(save.error, "Could not save.")}</Notice>}
      </SettingsSection>
    </>
  );
}

export default function AccountPage() {
  return (
    <Page as="main">
      <Account>
        <Details />
      </Account>
    </Page>
  );
}
